/**
 * Assistant WebSocket Service — migrated to A2A v1.0 (A-0007.05).
 *
 * Before: POSTed /assistant/execute then opened a WebSocket to poll task state.
 *   Both that endpoint and the track-auto-assistant WebSocket were REMOVED
 *   earlier and this service has been broken for some time.
 *
 * After: non-streaming A2A SendMessage via the shared a2aClient. Returns the
 *   executor result in a backward-compatible wrapper so the 4 consumer
 *   components (ChatSection, CreateTableAI, AITeacher, CodeChatBox) receive
 *   the same-shaped object they previously got from the WebSocket SUCCESS
 *   message.
 *
 * Breaking signature change:
 *     setAssistant(id)                     → setAssistant(id, token)
 *     sendToAssistant(org, sub, opcode, inputData)  — unchanged signature
 *         (token pulled from the value set via setAssistant)
 */
import * as a2aClient from 'shared/api/services/BEX/a2aClient';

class AssistantWsService {
    constructor() {
        this.assistant = null;
        this.assistantToken = null;
    }

    /**
     * Register an assistant for subsequent sendToAssistant calls.
     * @param {string} assistant_id
     * @param {string} [assistant_token] — Bearer token from the assistant's L1 config
     */
    setAssistant = (assistant_id, assistant_token = null) => {
        this.assistant = assistant_id;
        this.assistantToken = assistant_token;
    };

    /**
     * Send a flat context dict to an assistant's opcode via A2A SendMessage.
     *
     * @param {string} organizationId
     * @param {string} subOrganizationId
     * @param {string} opcodeId
     * @param {Object} inputData — flat context dict. Wrapped as typed_input.context.
     * @returns {Promise<Object>} — { state, taskId, output, errors, task }
     */
    async sendToAssistant(organizationId, subOrganizationId, opcodeId, inputData) {
        if (!this.assistant) {
            throw new ReferenceError('No BeX assistant was set — call setAssistant(id, token) first.');
        }
        if (!opcodeId) {
            throw new ReferenceError('No OpCode ID provided.');
        }
        if (!this.assistantToken) {
            // Pre-existing broken case — this service historically worked
            // against an unauthenticated legacy endpoint. A2A requires a
            // Bearer token; consumers must pass one via setAssistant.
            console.warn(
                '[assistantWsService] No assistant_token set — A2A will reject. '
                + 'Update callers to use setAssistant(id, token).',
            );
        }

        const cfg = {
            orgId: organizationId,
            subOrgId: subOrganizationId,
            assistantName: this.assistant,
            assistantToken: this.assistantToken || '',
        };
        const opts = {
            typedInput: {
                context: (inputData && typeof inputData === 'object') ? inputData : {},
            },
            opcodeId,
        };

        try {
            const result = await a2aClient.sendMessage(cfg, opts);
            return result;
        } catch (error) {
            console.error('[assistantWsService] A2A SendMessage failed:', error);
            throw error;
        }
    }
}

const assistantWsService = new AssistantWsService();
export { assistantWsService };
